import { useMemo } from "react";
import { Floor, FloorAssignment, Seat, Booking } from "./types";
import { toLocalDateString } from "../../../utils/dateUtils";

export const useFloorFiltering = (
  floors: Floor[],
  floorAssignments: FloorAssignment[],
  selectedDate: Date
) => {
  // Filter floors based on date assignment, visibility, and seat capacity
  const availableFloors = useMemo(() => {
    const dateStr = toLocalDateString(selectedDate);
    const dateAssignments = floorAssignments.filter(a => a.date === dateStr);
    
    // Only show floors if there are assignments for this date
    if (dateAssignments.length > 0) {
      // Show all floors assigned to this date that are:
      // 1. Visible to users (canView = true)
      // 2. Have available seats
      // 3. Floor is active
      const assignedFloorIds = dateAssignments
        .filter(a => a.canView && a.availableSeats > 0)  // Changed from isVisibleToUsers to canView
        .map(a => a.floorId);
      
      return floors.filter(f => assignedFloorIds.includes(f.id) && f.isActive);
    }
    
    // If no assignments for this date, return empty array
    return [];
  }, [floors, floorAssignments, selectedDate]);

  return { availableFloors };
};

export const useAvailableSeats = (
  floors: Floor[],
  floorAssignments: FloorAssignment[],
  bookingsOnDate: Booking[],
  selectedDate: Date
) => {
  // Get available seats for a specific floor on the selected date
  const getAvailableSeatsForFloor = (floorId: string): Seat[] => {
    const dateStr = toLocalDateString(selectedDate);
    const assignment = floorAssignments.find(a => a.date === dateStr && a.floorId === floorId);
    const floor = floors.find(f => f.id === floorId);
    
    if (!floor || !floor.seats || !assignment) return [];
    
    // Get all seats that are not booked
    const unbookedSeats = floor.seats.filter(s => 
      !bookingsOnDate.some(b => b.seatId === s.id)
    );
    
    // Calculate how many seats are still available based on manager's limit
    const bookedCount = bookingsOnDate.filter(b => b.floorId === floorId).length;
    const remainingCapacity = assignment.availableSeats - bookedCount;
    
    // Return only up to the remaining capacity (manager's limit)
    // This ensures we show exactly the number of seats the manager allocated
    return unbookedSeats.slice(0, Math.max(0, remainingCapacity));
  };

  // Check if booking is allowed for a specific floor
  const canBookFloor = (floorId: string): boolean => {
    const dateStr = toLocalDateString(selectedDate);
    const assignment = floorAssignments.find(a => a.date === dateStr && a.floorId === floorId);
    
    // If no assignment or canBook is false, booking is not allowed
    return assignment?.canBook ?? false;
  };

  return { getAvailableSeatsForFloor, canBookFloor };
};
